// Physics → Section 10: Waves (KS3, sourced from the Grade9Ready
// science PDF). 9 topics as specified by the user. Same rich-content
// template as the other section files.
import authored from './authoredTopics.json';

const AUTHORED_KEY = 'physics/waves';
const AUTHORED = (authored && authored[AUTHORED_KEY]) || [];
const AUTHORED_BY_ID = Object.fromEntries(AUTHORED.map((t) => [t.id, t]));

// =====================================================================
// 10.1  WATER WAVES
// =====================================================================
const WATER_WAVES_TOPIC = {
  id: 'phys-10.1',
  title: 'Water Waves',
  slug: 'water-waves',
  pageRef: 'Section 10 · Topic 1',
  difficulty: 'foundation',
  diagram: null,
  learn: {
    chunks: [
      {
        heading: 'What is a wave?',
        body:
          'A wave is a way that energy travels from one place to another, WITHOUT carrying matter with it. A leaf floating on a lake bobs up and down as a ripple passes — but the leaf itself doesn\u2019t move along with the wave. Only the energy moves through.',
        check: {
          question: 'A wave on water transfers…',
          options: ['Water from one side to the other', 'Energy without moving the water across', 'Heat only', 'Mass only'],
          answer: 'Energy without moving the water across',
          explainer: 'The water just oscillates up/down — energy is what travels.',
        },
      },
      {
        heading: 'Wave parts',
        body:
          'Every wave has key features. Wavelength (λ) = distance from one peak (crest) to the next. Amplitude = height from rest position to peak (bigger amplitude = more energy). Frequency = number of complete waves per second, in hertz (Hz). Period = time for one wave to pass.',
        check: {
          question: 'A wave has 3 peaks pass by in 1 second. Frequency?',
          options: ['1 Hz', '2 Hz', '3 Hz', '6 Hz'],
          answer: '3 Hz',
          explainer: 'Frequency = waves per second.',
        },
      },
      {
        heading: 'Transverse vs longitudinal',
        body:
          'Water waves are TRANSVERSE — the water moves perpendicular (up/down) to the direction the wave travels. Most waves you see are transverse: water, light, ripples on a rope. Sound is the opposite — LONGITUDINAL — particles vibrate back and forth along the direction the wave travels.',
        check: {
          question: 'Water waves are…',
          options: ['Longitudinal', 'Transverse', 'Both', 'Neither'],
          answer: 'Transverse',
          explainer: 'Water moves up/down while the wave moves sideways.',
        },
      },
    ],
    keyPoints: [
      'A wave transfers energy without transferring matter',
      'Wavelength = peak-to-peak distance',
      'Amplitude = height from rest to peak (bigger = more energy)',
      'Frequency (Hz) = waves per second',
      'Water waves are TRANSVERSE',
    ],
    workedExamples: [
      {
        problem: 'A water wave has 5 crests passing a point every 2 seconds. Find its frequency.',
        steps: [
          { step: 1, instruction: 'Per second', working: '5 crests ÷ 2 s = 2.5 per second' },
          { step: 2, instruction: 'Convert', working: 'Frequency = 2.5 Hz' },
        ],
        answer: '2.5 Hz.',
        commonMistake: 'Forgetting to divide by the time — frequency is per SECOND.',
      },
    ],
    whyItWorks:
      'Waves are nature\u2019s most efficient delivery van — they carry energy across oceans, through air, and across space without dragging the medium along. Once you understand wavelength, frequency and amplitude, you understand the rest of the section.',
  },
  glossary: [
    { term: 'Wave', definition: 'A disturbance that transfers energy without transferring matter.' },
    { term: 'Wavelength (λ)', definition: 'Distance from one peak to the next.' },
    { term: 'Amplitude', definition: 'Height from rest to peak; bigger = more energy.' },
    { term: 'Frequency', definition: 'Number of waves per second; unit Hz.' },
    { term: 'Transverse wave', definition: 'A wave where the medium moves perpendicular to the direction of travel.' },
  ],
  examQuestion: {
    prompt:
      "(a) Define amplitude and wavelength. (b) Explain why a leaf on water doesn't travel along with the waves. (c) State whether water waves are transverse or longitudinal. (4 marks)",
    maxMarks: 4,
    markSchemePoints: [
      'Amplitude = height from rest to peak (more energy if bigger)',
      'Wavelength = distance from one peak to the next',
      'Water just bobs up and down; energy moves on but matter does not',
      'Transverse',
    ],
  },
  practice: {
    warmUp: [
      { id: 'phys-10.1.w1', type: 'text', question: 'Distance between two wave peaks is the ___ .', answer: 'wavelength', hint1: '10 letters', hint2: 'Symbol λ', hint3: 'Starts with w' },
      { id: 'phys-10.1.w2', type: 'choice', question: 'Bigger amplitude means…', options: ['Longer wave', 'More energy', 'Higher frequency', 'Faster speed'], answer: 'More energy', hint1: 'Energy proportional', hint2: 'Taller crest', hint3: 'Stronger wave' },
      { id: 'phys-10.1.w3', type: 'text', question: 'Unit of frequency is ___ .', answer: 'Hz', hint1: '2 letters', hint2: 'Per second', hint3: 'Hertz' },
    ],
    level1: [
      { id: 'phys-10.1.1', type: 'choice', question: 'A wave transfers…', options: ['Mass', 'Energy', 'Heat only', 'Air'], answer: 'Energy', hint1: 'Topic essential', hint2: 'Not matter', hint3: '6 letters' },
      { id: 'phys-10.1.2', type: 'number', question: '20 waves in 4 s. Frequency (Hz)?', answer: '5', hint1: '20 ÷ 4', hint2: 'Single digit', hint3: 'Five' },
    ],
    level2: [
      { id: 'phys-10.1.3', type: 'choice', question: 'Direction of water motion vs wave direction is…', options: ['Same', 'Perpendicular (transverse)', 'Opposite', 'Random'], answer: 'Perpendicular (transverse)', hint1: 'Up/down vs sideways', hint2: 'Right angles', hint3: 'Transverse pattern' },
      { id: 'phys-10.1.4', type: 'text', question: 'Time for one wave to pass is its ___ .', answer: 'period', hint1: '6 letters', hint2: '1 ÷ frequency', hint3: 'Starts with p' },
    ],
    level3: [
      { id: 'phys-10.1.5', type: 'choice', question: 'A leaf floating on a passing wave…', options: ['Travels with the wave', 'Bobs up and down in place', 'Sinks immediately', 'Speeds up'], answer: 'Bobs up and down in place', hint1: 'No matter transfer', hint2: 'Just oscillates', hint3: 'Wave passes through' },
      { id: 'phys-10.1.6', type: 'text', question: 'Number of waves passing per second is ___ .', answer: 'frequency', hint1: '9 letters', hint2: 'Symbol f', hint3: 'Starts with f' },
    ],
  },
};

// =====================================================================
// 10.2  LIGHT WAVES
// =====================================================================
const LIGHT_TOPIC = {
  id: 'phys-10.2',
  title: 'Light Waves',
  slug: 'light-waves',
  pageRef: 'Section 10 · Topic 2',
  difficulty: 'foundation',
  diagram: null,
  learn: {
    chunks: [
      {
        heading: 'Light is a transverse wave',
        body:
          'Light is a transverse wave too. Unlike water or sound waves, light needs no medium — it can travel through a vacuum (which is how sunlight crosses empty space to reach Earth). The speed of light in a vacuum is 300,000,000 m/s (3 × 10⁸ m/s) — the universal speed limit.',
        check: {
          question: 'Light can travel through…',
          options: ['Air only', 'Water only', 'Air, water and a vacuum', 'Magnets only'],
          answer: 'Air, water and a vacuum',
          explainer: 'Light works in a vacuum — sunlight reaches us across empty space.',
        },
      },
      {
        heading: 'Speed and wavelength',
        body:
          'Light slows down a bit when it enters glass or water (about 200,000,000 m/s in glass). The visible spectrum runs from red (longest wavelength, ~700 nm) through orange, yellow, green, blue, indigo, violet (shortest, ~400 nm). Beyond visible: infrared (longer than red), and ultraviolet (shorter than violet) — both invisible to humans.',
        check: {
          question: 'Which colour has the LONGEST wavelength?',
          options: ['Red', 'Green', 'Blue', 'Violet'],
          answer: 'Red',
          explainer: 'Red ~700 nm, violet ~400 nm.',
        },
      },
      {
        heading: 'How light travels',
        body:
          'In a uniform medium, light travels in STRAIGHT LINES. That\u2019s why we have shadows — light cannot bend around objects (it can only diffract a tiny bit). Many physics problems use ray diagrams: drawing arrows along the path of light from a source to its destination.',
        check: {
          question: 'Light travels in…',
          options: ['Random directions', 'Straight lines (in a uniform medium)', 'Curves', 'Spirals'],
          answer: 'Straight lines (in a uniform medium)',
          explainer: 'That\u2019s why shadows form sharp edges.',
        },
      },
    ],
    keyPoints: [
      'Light = transverse wave; needs no medium',
      'Speed in vacuum = 3 × 10⁸ m/s (universal limit)',
      'Visible spectrum: red (~700 nm) → violet (~400 nm)',
      'Light travels in straight lines',
      'IR (longer than red) and UV (shorter than violet) are invisible',
    ],
    workedExamples: [
      {
        problem: 'Calculate roughly how long sunlight takes to reach Earth (Sun is 150 million km away).',
        steps: [
          { step: 1, instruction: 'Distance', working: '150,000,000 km = 1.5 × 10¹¹ m' },
          { step: 2, instruction: 'Time = distance ÷ speed', working: '1.5 × 10¹¹ ÷ 3 × 10⁸ = 500 s' },
          { step: 3, instruction: 'Convert', working: '500 s ÷ 60 ≈ 8.3 min' },
        ],
        answer: 'About 8.3 minutes.',
        commonMistake: 'Forgetting to convert km → m before dividing.',
      },
    ],
    whyItWorks:
      'Light is the universal messenger. Almost everything you SEE is photons hitting your eye after bouncing off a surface, and most of the universe\u2019s information reaches us as light from distant stars.',
  },
  glossary: [
    { term: 'Light', definition: 'A transverse electromagnetic wave that can travel through a vacuum.' },
    { term: 'Speed of light', definition: '~3 × 10⁸ m/s in a vacuum.' },
    { term: 'Visible spectrum', definition: 'Range of wavelengths the human eye can see (~400–700 nm).' },
    { term: 'Infrared', definition: 'Light with a longer wavelength than red — invisible.' },
    { term: 'Ultraviolet', definition: 'Light with a shorter wavelength than violet — invisible; can damage cells.' },
  ],
  examQuestion: {
    prompt:
      '(a) State the speed of light in a vacuum. (b) State whether light is transverse or longitudinal. (c) Explain why we see shadows. (4 marks)',
    maxMarks: 4,
    markSchemePoints: [
      '300,000,000 m/s (3 × 10⁸ m/s)',
      'Transverse',
      'Light travels in straight lines',
      'so cannot bend around objects → an opaque object blocks light, leaving a shadow behind it',
    ],
  },
  practice: {
    warmUp: [
      { id: 'phys-10.2.w1', type: 'text', question: 'Light is a ___ wave.', answer: 'transverse', hint1: '10 letters', hint2: 'Same as water', hint3: 'Starts with t' },
      { id: 'phys-10.2.w2', type: 'choice', question: 'Speed of light in vacuum?', options: ['300 m/s', '3 km/s', '300,000 m/s', '300,000,000 m/s'], answer: '300,000,000 m/s', hint1: '3 × 10⁸', hint2: 'Universal limit', hint3: 'Very fast' },
      { id: 'phys-10.2.w3', type: 'choice', question: 'Longest visible wavelength?', options: ['Violet', 'Red', 'Green', 'Blue'], answer: 'Red', hint1: '~700 nm', hint2: 'Top of rainbow', hint3: 'R in Roy G Biv' },
    ],
    level1: [
      { id: 'phys-10.2.1', type: 'choice', question: 'Can light travel through a vacuum?', options: ['No', 'Yes', 'Only in liquids', 'Only with sound'], answer: 'Yes', hint1: 'Reaches us from space', hint2: 'No medium needed', hint3: 'Sunlight evidence' },
      { id: 'phys-10.2.2', type: 'text', question: 'Light shorter than violet is called ___ .', answer: 'ultraviolet', hint1: '11 letters', hint2: 'UV', hint3: 'Damages skin' },
    ],
    level2: [
      { id: 'phys-10.2.3', type: 'choice', question: 'Why do shadows form?', options: ['Air absorbs', 'Light travels in straight lines', 'Light bends', 'Optical illusion'], answer: 'Light travels in straight lines', hint1: 'Cannot bend around', hint2: 'Linear travel', hint3: 'Block creates shadow' },
      { id: 'phys-10.2.4', type: 'choice', question: 'Light SLOWS DOWN when going from air to glass. Speed?', options: ['Faster', 'Stays the same', 'About 200 million m/s', 'Stops'], answer: 'About 200 million m/s', hint1: 'Less than vacuum speed', hint2: '~2 × 10⁸ m/s', hint3: 'Optical density up' },
    ],
    level3: [
      { id: 'phys-10.2.5', type: 'choice', question: 'Light from the Sun takes about…', options: ['8 seconds', '8 minutes', '8 hours', '8 days'], answer: '8 minutes', hint1: '~500 s', hint2: 'A few minutes', hint3: 'Round-trip back is 16 min' },
      { id: 'phys-10.2.6', type: 'text', question: 'Light longer than red wavelength is ___ .', answer: 'infrared', hint1: '8 letters', hint2: 'IR', hint3: 'Heat radiation' },
    ],
  },
};

// =====================================================================
// 10.3  REFLECTION
// =====================================================================
const REFLECTION_TOPIC = {
  id: 'phys-10.3',
  title: 'Reflection',
  slug: 'reflection',
  pageRef: 'Section 10 · Topic 3',
  difficulty: 'foundation',
  diagram: null,
  learn: {
    chunks: [
      {
        heading: 'What is reflection?',
        body:
          'Reflection is when a wave bounces off a surface. Most surfaces reflect SOME light — that\u2019s how we see things that don\u2019t glow. Smooth shiny surfaces (mirrors) give a clear reflection (specular). Rough surfaces (paper, wall) scatter light in many directions (diffuse) — same reflection, different geometry.',
        check: {
          question: 'You see a wall because it…',
          options: ['Glows', 'Reflects light into your eyes', 'Absorbs light', 'Bends light'],
          answer: 'Reflects light into your eyes',
          explainer: 'Walls don\u2019t make light — they bounce it.',
        },
      },
      {
        heading: 'The law of reflection',
        body:
          'When light hits a surface: angle of INCIDENCE = angle of REFLECTION. Both are measured from the NORMAL — an imaginary line perpendicular to the surface. So if light comes in at 30° from the normal, it bounces out at 30° on the other side. This is the LAW OF REFLECTION — true for all flat reflective surfaces.',
        check: {
          question: 'Light hits a mirror at 40° from the normal. The reflected angle is…',
          options: ['20°', '40°', '60°', '90°'],
          answer: '40°',
          explainer: 'Angle of incidence = angle of reflection.',
        },
      },
      {
        heading: 'Mirrors and images',
        body:
          'A flat (plane) mirror gives an image that is: same size as the object; the SAME distance behind the mirror as the object is in front; LATERALLY INVERTED (left/right swapped); and VIRTUAL (cannot be projected onto a screen — it just appears behind the glass). That\u2019s why writing on your shirt reads backwards in the mirror.',
        check: {
          question: 'A plane mirror image is…',
          options: ['Real and bigger', 'Virtual and same size', 'Real and smaller', 'Upside down'],
          answer: 'Virtual and same size',
          explainer: 'Plane mirrors give virtual same-size images that are laterally inverted.',
        },
      },
    ],
    keyPoints: [
      'Reflection = wave bouncing off a surface',
      'Smooth = specular; rough = diffuse',
      'Angle of incidence = angle of reflection (from normal)',
      'Plane mirror image: same size, virtual, laterally inverted',
      'Image distance = object distance (behind the mirror)',
    ],
    workedExamples: [
      {
        problem: 'A ray hits a mirror at 25° from the surface. Find the angle of reflection from the normal.',
        steps: [
          { step: 1, instruction: 'Convert', working: 'Angle from surface = 25°, so from normal = 90 − 25 = 65°' },
          { step: 2, instruction: 'Apply law', working: 'Angle of reflection = angle of incidence' },
          { step: 3, instruction: 'Result', working: '65°' },
        ],
        answer: '65° from the normal.',
        commonMistake: 'Measuring from the surface instead of the normal — angles are ALWAYS from the normal.',
      },
    ],
    whyItWorks:
      'Reflection follows simple geometry. Once you know the normal and the incoming ray, the outgoing ray is fully determined. That\u2019s why every periscope, mirror selfie and rear-view mirror works exactly as predicted.',
  },
  glossary: [
    { term: 'Reflection', definition: 'A wave bouncing off a surface.' },
    { term: 'Angle of incidence', definition: 'Angle between incoming ray and normal.' },
    { term: 'Angle of reflection', definition: 'Angle between reflected ray and normal.' },
    { term: 'Normal', definition: 'An imaginary line perpendicular to the surface at the point of contact.' },
    { term: 'Plane mirror', definition: 'A flat mirror.' },
    { term: 'Virtual image', definition: 'An image that cannot be projected onto a screen.' },
  ],
  examQuestion: {
    prompt:
      '(a) State the law of reflection. (b) Light hits a plane mirror at an angle of 35° from the normal. State the angle of reflection. (c) Describe TWO features of the image in a flat mirror. (4 marks)',
    maxMarks: 4,
    markSchemePoints: [
      'Angle of incidence = angle of reflection (both measured from the normal)',
      '35°',
      'Image is virtual / same size as the object',
      'Image is laterally inverted (left/right swapped)',
    ],
  },
  practice: {
    warmUp: [
      { id: 'phys-10.3.w1', type: 'text', question: 'Imaginary line perpendicular to surface is the ___ .', answer: 'normal', hint1: '6 letters', hint2: 'Right angles', hint3: 'Starts with n' },
      { id: 'phys-10.3.w2', type: 'choice', question: 'Angle of incidence is measured from…', options: ['Surface', 'Normal', 'Mirror edge', 'Light bulb'], answer: 'Normal', hint1: 'Perpendicular to surface', hint2: 'Standard reference', hint3: 'Same word as topic' },
      { id: 'phys-10.3.w3', type: 'number', question: 'Light hits at 50° from normal. Reflected angle?', answer: '50', hint1: 'Equal angles', hint2: 'Two digits', hint3: 'Same as incidence' },
    ],
    level1: [
      { id: 'phys-10.3.1', type: 'choice', question: 'A plane mirror image is…', options: ['Real', 'Virtual', 'Inverted upside down', 'Smaller'], answer: 'Virtual', hint1: 'Behind the mirror', hint2: 'No screen needed', hint3: 'Cannot project' },
      { id: 'phys-10.3.2', type: 'choice', question: 'Words read backwards in a mirror because the image is…', options: ['Upside down', 'Larger', 'Laterally inverted', 'Smaller'], answer: 'Laterally inverted', hint1: 'Left-right swap', hint2: 'Mirror flip', hint3: 'Same as text reversal' },
    ],
    level2: [
      { id: 'phys-10.3.3', type: 'choice', question: 'Diffuse reflection happens at…', options: ['Smooth shiny surfaces', 'Rough surfaces', 'Mirrors only', 'Glass only'], answer: 'Rough surfaces', hint1: 'Scatters light', hint2: 'Paper-like', hint3: 'Many directions' },
      { id: 'phys-10.3.4', type: 'number', question: 'Ray hits mirror at 30° to surface. Angle of reflection from normal?', answer: '60', hint1: '90 − 30', hint2: 'Two digits', hint3: 'Sixty' },
    ],
    level3: [
      { id: 'phys-10.3.5', type: 'choice', question: 'In a plane mirror, image distance vs object distance is…', options: ['Larger', 'Smaller', 'The same', 'Zero'], answer: 'The same', hint1: 'Equal distances', hint2: 'Symmetrical', hint3: 'Behind = in front' },
      { id: 'phys-10.3.6', type: 'text', question: 'A flat mirror is also called a ___ mirror.', answer: 'plane', hint1: '5 letters', hint2: 'Not curved', hint3: 'Starts with p' },
    ],
  },
};

// =====================================================================
// 10.4  REFRACTION
// =====================================================================
const REFRACTION_TOPIC = {
  id: 'phys-10.4',
  title: 'Refraction',
  slug: 'refraction',
  pageRef: 'Section 10 · Topic 4',
  difficulty: 'higher',
  diagram: null,
  learn: {
    chunks: [
      {
        heading: 'Bending light',
        body:
          'Refraction is the BENDING of a wave when it crosses a boundary between materials. It happens because the wave changes SPEED. Light slows down when it enters denser materials (water, glass) and speeds up when it leaves them. The wave bends TOWARDS the normal when slowing down, and AWAY from the normal when speeding up.',
        check: {
          question: 'Light entering glass from air will…',
          options: ['Speed up', 'Slow down and bend towards normal', 'Stay the same', 'Stop'],
          answer: 'Slow down and bend towards normal',
          explainer: 'Glass is denser → light slows → bends toward normal.',
        },
      },
      {
        heading: 'Real-world refraction',
        body:
          'A pencil in a glass of water looks "broken" because light from the underwater part bends as it leaves the water — making the pencil appear shifted. A swimming pool looks shallower than it is for the same reason. Lenses (in glasses, cameras, telescopes) use refraction to focus light into clear images.',
        check: {
          question: 'A pencil in water LOOKS broken because…',
          options: ['Water bends pencils', 'Light refracts as it leaves water, shifting where the underwater part appears', 'It is an illusion of shadow', 'The pencil floats'],
          answer: 'Light refracts as it leaves water, shifting where the underwater part appears',
          explainer: 'Brain interprets light as travelling in a straight line — but it actually bent at the water surface.',
        },
      },
      {
        heading: 'Lenses',
        body:
          'A converging (convex) lens bends parallel rays inwards to a single focal point — used to focus light in cameras, magnifying glasses and the human eye. A diverging (concave) lens spreads parallel rays outwards as if they came from a focal point behind — used in glasses to correct short-sightedness.',
        check: {
          question: 'Which lens focuses parallel light to a single point?',
          options: ['Concave', 'Convex (converging)', 'Plane', 'No lens does this'],
          answer: 'Convex (converging)',
          explainer: 'Convex lenses bring rays together — perfect for cameras and magnifiers.',
        },
      },
    ],
    keyPoints: [
      'Refraction = bending of a wave at a boundary',
      'Caused by a change of speed',
      'Slows down (e.g. air → glass) → bends towards the normal',
      'Speeds up (e.g. glass → air) → bends away from the normal',
      'Convex lens converges; concave lens diverges',
    ],
    workedExamples: [
      {
        problem: 'A ray of light enters a glass block from air. State what happens to (a) speed, (b) wavelength, (c) frequency, (d) direction.',
        steps: [
          { step: 1, instruction: 'Speed', working: 'Slows down (slower in glass)' },
          { step: 2, instruction: 'Wavelength', working: 'Decreases (gets shorter — same frequency, slower speed)' },
          { step: 3, instruction: 'Frequency', working: 'Stays the same (set by the source)' },
          { step: 4, instruction: 'Direction', working: 'Bends TOWARDS the normal' },
        ],
        answer: 'Slows; wavelength decreases; frequency unchanged; bends towards the normal.',
        commonMistake: 'Saying frequency changes. It does NOT — the source sets it.',
      },
    ],
    whyItWorks:
      'Refraction is everywhere — your glasses, contact lenses, microscopes, even rainbows (different colours bend by different amounts when going through raindrops). Once you know light bends with speed change, you can predict every optical effect.',
  },
  glossary: [
    { term: 'Refraction', definition: 'The bending of a wave at a boundary between materials, caused by speed change.' },
    { term: 'Convex (converging) lens', definition: 'A lens that brings parallel rays to a focus.' },
    { term: 'Concave (diverging) lens', definition: 'A lens that spreads parallel rays apart.' },
    { term: 'Focal point', definition: 'The point where a converging lens focuses parallel rays.' },
  ],
  examQuestion: {
    prompt:
      '(a) Define refraction. (b) Explain why a straight pencil looks bent in a glass of water. (c) State the difference between a convex and concave lens. (4 marks)',
    maxMarks: 4,
    markSchemePoints: [
      'Refraction = bending of a wave at a boundary due to speed change',
      'Light from the underwater part of the pencil bends as it leaves the water',
      'so the brain sees the pencil at a slightly different position → looks bent',
      'Convex (converging) brings light to a focus; concave (diverging) spreads it out',
    ],
  },
  practice: {
    warmUp: [
      { id: 'phys-10.4.w1', type: 'text', question: 'Bending of light at a boundary is called ___ .', answer: 'refraction', hint1: '10 letters', hint2: 'Topic title', hint3: 'Starts with r' },
      { id: 'phys-10.4.w2', type: 'choice', question: 'Light entering glass from air…', options: ['Speeds up', 'Slows down', 'Stays the same', 'Stops'], answer: 'Slows down', hint1: 'Denser material', hint2: 'Lower speed', hint3: 'Reduces velocity' },
      { id: 'phys-10.4.w3', type: 'choice', question: 'Convex lenses…', options: ['Spread light apart', 'Focus light to a point', 'Reflect light', 'Block light'], answer: 'Focus light to a point', hint1: 'Converging', hint2: 'Magnifying glass', hint3: 'Used in cameras' },
    ],
    level1: [
      { id: 'phys-10.4.1', type: 'choice', question: 'Light leaves water to enter air. It bends…', options: ['Towards the normal', 'Away from the normal', 'No bending', 'Stops'], answer: 'Away from the normal', hint1: 'Speeds up', hint2: 'Less dense exit', hint3: 'Opposite of entering' },
      { id: 'phys-10.4.2', type: 'text', question: 'Concave lenses are used to correct ___-sightedness.', answer: 'short', hint1: '5 letters', hint2: 'Myopia', hint3: 'Starts with s' },
    ],
    level2: [
      { id: 'phys-10.4.3', type: 'choice', question: 'When light refracts, which property does NOT change?', options: ['Speed', 'Wavelength', 'Frequency', 'Direction'], answer: 'Frequency', hint1: 'Set by source', hint2: 'Unchanged', hint3: 'Speed and wavelength change together' },
      { id: 'phys-10.4.4', type: 'choice', question: 'A pool looks ___ shallower than it is.', options: ['Less', 'Always', 'Twice as', 'Much'], answer: 'Always', hint1: 'Common refraction effect', hint2: 'Light bends', hint3: 'True for all swim pools' },
    ],
    level3: [
      { id: 'phys-10.4.5', type: 'choice', question: 'Why do rainbows form?', options: ['Reflection only', 'Different colours refract by different amounts in raindrops', 'Magnetism', 'Magic'], answer: 'Different colours refract by different amounts in raindrops', hint1: 'Dispersion', hint2: 'Each wavelength bends differently', hint3: 'Spectrum split' },
      { id: 'phys-10.4.6', type: 'text', question: 'A convex lens is also called a ___ lens.', answer: 'converging', hint1: '10 letters', hint2: 'Brings together', hint3: 'Starts with c' },
    ],
  },
};

// =====================================================================
// 10.5  HOW WE SEE
// =====================================================================
const HOW_WE_SEE_TOPIC = {
  id: 'phys-10.5',
  title: 'How We See',
  slug: 'how-we-see',
  pageRef: 'Section 10 · Topic 5',
  difficulty: 'higher',
  diagram: null,
  learn: {
    chunks: [
      {
        heading: 'Light into the eye',
        body:
          'We see things because light from a source enters our eye. The light enters through the cornea (clear front), passes through the pupil (the dark hole in your iris), is focused by the lens, and forms an image on the retina at the back. The retina contains light-sensitive cells (rods and cones) that turn the light into nerve signals; the optic nerve carries them to the brain.',
        check: {
          question: 'The image we see is formed on the…',
          options: ['Cornea', 'Pupil', 'Retina', 'Lens'],
          answer: 'Retina',
          explainer: 'Retina = the screen at the back of the eye.',
        },
      },
      {
        heading: 'Light sources vs reflectors',
        body:
          'Some objects PRODUCE light: the Sun, lamps, fires, screens. Most objects don\u2019t — they only REFLECT light from a source into your eye. You see a tree because sunlight bounces off it; a wall because room light bounces off it. Without a light source you cannot see — that\u2019s why a totally dark room is invisible.',
        check: {
          question: 'You can see this textbook because…',
          options: ['It glows', 'Light reflects off it into your eyes', 'Eyes emit light', 'Magic'],
          answer: 'Light reflects off it into your eyes',
          explainer: 'Books are reflectors, not sources.',
        },
      },
      {
        heading: 'Focusing and short/long sight',
        body:
          'The eye lens changes shape (called accommodation) to focus on near or far objects. Short-sightedness (myopia): the eye focuses light BEFORE the retina — distant things look blurry. Corrected with concave (diverging) lenses. Long-sightedness (hyperopia): light focuses BEHIND the retina — near things look blurry. Corrected with convex (converging) lenses.',
        check: {
          question: 'Short-sighted people see best at…',
          options: ['Far distances', 'Near distances', 'No focus possible', 'Equal at all distances'],
          answer: 'Near distances',
          explainer: 'Hence "short-sighted" — see near things clearly, far things blurred.',
        },
      },
    ],
    keyPoints: [
      'Eye structure: cornea → pupil → lens → retina',
      'Retina has rods (low light) and cones (colour)',
      'Most things are reflectors, not sources',
      'Short-sighted (myopia): corrected with concave lenses',
      'Long-sighted (hyperopia): corrected with convex lenses',
    ],
    workedExamples: [
      {
        problem: 'Ali can see books clearly but distant signs look blurry. Identify the condition and the type of lens that could correct it.',
        steps: [
          { step: 1, instruction: 'Diagnose', working: 'Near OK, far blurry → short-sighted (myopia)' },
          { step: 2, instruction: 'Choose lens', working: 'Need to spread light slightly so it focuses ON the retina rather than in front' },
          { step: 3, instruction: 'Lens type', working: 'Concave (diverging) lens' },
        ],
        answer: 'Short-sighted; concave (diverging) lens.',
        commonMistake: 'Mixing up convex and concave for the two conditions. Convex = stronger focus (long sight); concave = less focus (short sight).',
      },
    ],
    whyItWorks:
      'The eye is a tiny biological camera. Once you understand it has a lens that focuses light onto a "screen" (the retina), the rest follows: short or long sight is just a focusing error, fixed by adding a glasses lens that compensates.',
  },
  glossary: [
    { term: 'Cornea', definition: 'The clear front of the eye that does most of the focusing.' },
    { term: 'Pupil', definition: 'The hole at the centre of the iris that lets light in.' },
    { term: 'Lens (eye)', definition: 'A flexible focusing element in the eye.' },
    { term: 'Retina', definition: 'The screen at the back of the eye made of light-sensitive cells.' },
    { term: 'Myopia', definition: 'Short-sightedness; corrected with concave lenses.' },
    { term: 'Hyperopia', definition: 'Long-sightedness; corrected with convex lenses.' },
  ],
  examQuestion: {
    prompt:
      "(a) Trace the path of light through the eye. (b) Where in the eye is the image formed? (c) State the lens type used to correct short-sightedness. (4 marks)",
    maxMarks: 4,
    markSchemePoints: [
      'Cornea → pupil → lens → retina (correct order)',
      'On the retina',
      'Concave (diverging) lens',
      "(Causes light to spread slightly so it focuses on the retina, not in front of it)",
    ],
  },
  practice: {
    warmUp: [
      { id: 'phys-10.5.w1', type: 'text', question: 'The screen at the back of the eye is the ___ .', answer: 'retina', hint1: '6 letters', hint2: 'Where image forms', hint3: 'Starts with r' },
      { id: 'phys-10.5.w2', type: 'choice', question: 'A wall reflects light to your eye — wall is a…', options: ['Source', 'Reflector', 'Lens', 'Mirror'], answer: 'Reflector', hint1: 'Doesn\u2019t glow', hint2: 'Bounces light', hint3: 'Most objects are these' },
      { id: 'phys-10.5.w3', type: 'choice', question: 'Long-sightedness corrected with…', options: ['Concave lens', 'Convex lens', 'No lens', 'Flat lens'], answer: 'Convex lens', hint1: 'Brings rays together', hint2: 'Stronger focus', hint3: 'Same as magnifying glass' },
    ],
    level1: [
      { id: 'phys-10.5.1', type: 'choice', question: 'Light enters the eye through the…', options: ['Retina', 'Cornea', 'Optic nerve', 'Iris'], answer: 'Cornea', hint1: 'Clear front of eye', hint2: '6 letters', hint3: 'Outer dome' },
      { id: 'phys-10.5.2', type: 'text', question: 'Short-sightedness is also called ___ .', answer: 'myopia', hint1: '6 letters', hint2: 'Medical term', hint3: 'Starts with m' },
    ],
    level2: [
      { id: 'phys-10.5.3', type: 'choice', question: 'Cells in the retina that detect colour are…', options: ['Rods', 'Cones', 'Pupils', 'Lenses'], answer: 'Cones', hint1: '5 letters', hint2: 'For bright light/colour', hint3: 'Three types: red, green, blue' },
      { id: 'phys-10.5.4', type: 'choice', question: 'Adjusting eye lens shape to focus is called…', options: ['Refraction', 'Accommodation', 'Reflection', 'Diffusion'], answer: 'Accommodation', hint1: '13 letters', hint2: 'Eye-focusing', hint3: 'Starts with a' },
    ],
    level3: [
      { id: 'phys-10.5.5', type: 'choice', question: 'In short-sighted people, light focuses…', options: ['On the retina', 'Behind the retina', 'In front of the retina', 'Outside the eye'], answer: 'In front of the retina', hint1: 'Image forms early', hint2: 'Concave lens needed', hint3: 'Distant things blurry' },
      { id: 'phys-10.5.6', type: 'text', question: 'The dark hole at the centre of the iris is the ___ .', answer: 'pupil', hint1: '5 letters', hint2: 'Lets light in', hint3: 'Black centre' },
    ],
  },
};

// =====================================================================
// 10.6  COLOUR
// =====================================================================
const COLOUR_TOPIC = {
  id: 'phys-10.6',
  title: 'Colour',
  slug: 'colour',
  pageRef: 'Section 10 · Topic 6',
  difficulty: 'foundation',
  diagram: null,
  learn: {
    chunks: [
      {
        heading: 'White light is a mix',
        body:
          'White light (e.g. sunlight) is actually a MIXTURE of all the visible colours. A glass prism can split it into a rainbow (red, orange, yellow, green, blue, indigo, violet) — known as DISPERSION. Each colour refracts by a slightly different amount because each has a slightly different wavelength. Real rainbows form when raindrops act like millions of tiny prisms.',
        check: {
          question: 'White light is split by a prism into…',
          options: ['Just red and blue', 'A rainbow of all visible colours', 'Black', 'No change'],
          answer: 'A rainbow of all visible colours',
          explainer: 'Dispersion — different wavelengths bend by different amounts.',
        },
      },
      {
        heading: 'Why objects look coloured',
        body:
          'Coloured objects ABSORB some wavelengths and REFLECT others. A red apple looks red because it absorbs all the other colours of white light and reflects only red back to your eye. A blue T-shirt absorbs everything except blue. A WHITE object reflects ALL colours; a BLACK object absorbs them all (which is why black surfaces get hot in the Sun).',
        check: {
          question: 'A green leaf looks green because it…',
          options: ['Makes green light', 'Absorbs green and reflects others', 'Reflects green and absorbs others', 'Glows'],
          answer: 'Reflects green and absorbs others',
          explainer: 'Whatever is REFLECTED is the colour you see.',
        },
      },
      {
        heading: 'Filters',
        body:
          'A coloured filter only lets through one colour and absorbs the rest. A red filter passes red light and blocks the others. So a red apple under a red filter still looks red, but under a green filter the same apple looks BLACK (no red gets through, and the apple absorbs green too — so no light reaches your eye).',
        check: {
          question: 'A blue book is viewed through a red filter. How does it appear?',
          options: ['Blue', 'Red', 'Black', 'Purple'],
          answer: 'Black',
          explainer: 'Filter only passes red. Book absorbs red. No light reaches your eye → looks black.',
        },
      },
    ],
    keyPoints: [
      'White light = mix of all colours; prisms disperse it',
      'Objects absorb some wavelengths, reflect others',
      'A red object reflects RED, absorbs the rest',
      'White = reflects all; black = absorbs all',
      'Filters only let through one colour; absorb the rest',
    ],
    workedExamples: [
      {
        problem: 'A yellow flower is viewed under (a) white light, (b) blue light. Predict and explain its colour in each case.',
        steps: [
          { step: 1, instruction: 'Yellow flower properties', working: 'Yellow object reflects yellow (and red+green that mix to yellow); absorbs blue' },
          { step: 2, instruction: 'White light', working: 'All colours present → flower reflects yellow → looks yellow' },
          { step: 3, instruction: 'Blue light', working: 'Only blue present; flower absorbs blue → no light reflected → looks black' },
        ],
        answer: 'White light = yellow. Blue light = black.',
        commonMistake: 'Thinking the colour we see is the colour the object "creates" — actually it\u2019s the colour that\u2019s NOT absorbed.',
      },
    ],
    whyItWorks:
      'Colour is a beautiful link between physics (wavelengths of light) and biology (how cones in your retina decode them). Get the absorption-reflection rule, and every coloured object behaves predictably.',
  },
  glossary: [
    { term: 'Dispersion', definition: 'The splitting of white light into a spectrum of colours (e.g. by a prism).' },
    { term: 'Spectrum', definition: 'The range of colours produced by dispersing white light.' },
    { term: 'Filter', definition: 'A coloured material that lets through one colour and absorbs others.' },
    { term: 'Absorption', definition: 'When a material takes in light energy (often turning it to heat).' },
  ],
  examQuestion: {
    prompt:
      "A red apple is held under different lights. (a) Explain why it looks red under white light. (b) State and explain its colour under green light. (c) Explain why a black T-shirt feels hotter in the Sun than a white one. (5 marks)",
    maxMarks: 5,
    markSchemePoints: [
      'Apple absorbs all colours except red',
      'and reflects red into your eyes',
      'Under green light: apple absorbs green, no red present',
      'No light reflected → appears BLACK',
      'Black absorbs all colours → more energy absorbed → heats up faster',
    ],
  },
  practice: {
    warmUp: [
      { id: 'phys-10.6.w1', type: 'text', question: 'Splitting white light into colours is called ___ .', answer: 'dispersion', hint1: '10 letters', hint2: 'Prism effect', hint3: 'Starts with d' },
      { id: 'phys-10.6.w2', type: 'choice', question: 'A blue object reflects…', options: ['All colours', 'Only blue', 'No light', 'Only red'], answer: 'Only blue', hint1: 'Absorbs the rest', hint2: 'Whatever reflects = colour seen', hint3: 'Same idea as red apple' },
      { id: 'phys-10.6.w3', type: 'choice', question: 'A black surface in sunlight gets hotter because it…', options: ['Reflects all colours', 'Absorbs all colours', 'Glows', 'Is heavier'], answer: 'Absorbs all colours', hint1: 'No reflection', hint2: 'All energy absorbed', hint3: 'Hotter than white' },
    ],
    level1: [
      { id: 'phys-10.6.1', type: 'choice', question: 'A red filter only lets through…', options: ['Red light', 'Blue light', 'White light', 'No light'], answer: 'Red light', hint1: 'Same as filter colour', hint2: 'Absorbs others', hint3: 'Single wavelength range' },
      { id: 'phys-10.6.2', type: 'text', question: 'A white object ___ all colours of light.', answer: 'reflects', hint1: '8 letters', hint2: 'Bounces back', hint3: 'Starts with r' },
    ],
    level2: [
      { id: 'phys-10.6.3', type: 'choice', question: 'A green object under red light looks…', options: ['Green', 'Red', 'Black', 'White'], answer: 'Black', hint1: 'Object absorbs red', hint2: 'No green to reflect', hint3: 'No light bounces back' },
      { id: 'phys-10.6.4', type: 'choice', question: 'Why are different colours dispersed by a prism?', options: ['Random', 'Different wavelengths refract by different amounts', 'Different colours have different speeds in vacuum', 'It is reflection only'], answer: 'Different wavelengths refract by different amounts', hint1: 'Wavelength dependence', hint2: 'Refraction differs', hint3: 'Linked to wave speed in glass' },
    ],
    level3: [
      { id: 'phys-10.6.5', type: 'choice', question: 'Why does a rainbow have multiple colours?', options: ['Sun is multicoloured', 'Different colours bend by different amounts in raindrops', 'Reflection from clouds', 'Magic'], answer: 'Different colours bend by different amounts in raindrops', hint1: 'Dispersion', hint2: 'Same as prism', hint3: 'Each wavelength refracts uniquely' },
      { id: 'phys-10.6.6', type: 'text', question: 'Coloured material that lets one colour through is called a ___ .', answer: 'filter', hint1: '6 letters', hint2: 'Photography term', hint3: 'Starts with f' },
    ],
  },
};

// =====================================================================
// 10.7  SOUND
// =====================================================================
const SOUND_TOPIC = {
  id: 'phys-10.7',
  title: 'Sound',
  slug: 'sound',
  pageRef: 'Section 10 · Topic 7',
  difficulty: 'foundation',
  diagram: null,
  learn: {
    chunks: [
      {
        heading: 'Sound is a longitudinal wave',
        body:
          'Sound is a LONGITUDINAL wave caused by vibrating particles. The particles move BACK AND FORTH along the same direction the wave travels (compressions and rarefactions). Sound needs a medium — it CANNOT travel through a vacuum. That\u2019s why explosions in space wouldn\u2019t actually make any noise.',
        check: {
          question: 'Sound CANNOT travel through…',
          options: ['Air', 'Water', 'Steel', 'A vacuum'],
          answer: 'A vacuum',
          explainer: 'Sound needs particles to vibrate. No particles, no sound.',
        },
      },
      {
        heading: 'Speed of sound',
        body:
          'Sound travels at about 330 m/s in air at room temperature. It\u2019s much faster in liquids (~1500 m/s in water) and even faster in solids (~5000 m/s in steel). That\u2019s because particles are closer together — vibrations pass on faster. Sound is far slower than light. You see lightning before you hear thunder for this reason.',
        check: {
          question: 'You see lightning, then 6 seconds later hear thunder. Roughly how far away is the storm? (use 330 m/s)',
          options: ['1 km', '2 km', '5 km', '20 km'],
          answer: '2 km',
          explainer: 'Distance = speed × time = 330 × 6 = 1980 m ≈ 2 km.',
        },
      },
      {
        heading: 'Pitch and loudness',
        body:
          'Higher FREQUENCY → higher PITCH (a higher-pitched whistle is just a faster vibration). Higher AMPLITUDE → louder sound (more energy). Frequency is in Hz. Loudness in decibels (dB) — a logarithmic scale where 10 dB louder is roughly twice as loud, and 60 dB is normal conversation, 110 dB starts to damage hearing.',
        check: {
          question: 'A high-pitched whistle has…',
          options: ['Low frequency', 'High frequency', 'Big amplitude', 'No frequency'],
          answer: 'High frequency',
          explainer: 'Higher frequency = higher pitch.',
        },
      },
    ],
    keyPoints: [
      'Sound is longitudinal — particles vibrate along the wave direction',
      'Needs a medium (air, water, solid)',
      'Cannot travel through a vacuum',
      'Speed: ~330 m/s in air, ~1500 m/s in water, ~5000 m/s in steel',
      'Higher frequency = higher pitch',
      'Higher amplitude = louder',
    ],
    workedExamples: [
      {
        problem: 'A factory whistle is heard 1 km away exactly 3 seconds after it\u2019s blown. Calculate the speed of sound.',
        steps: [
          { step: 1, instruction: 'Distance', working: '1 km = 1000 m' },
          { step: 2, instruction: 'Speed = distance ÷ time', working: '1000 ÷ 3 ≈ 333 m/s' },
        ],
        answer: '~330 m/s.',
        commonMistake: 'Forgetting to convert km → m before dividing.',
      },
    ],
    whyItWorks:
      'Sound is just particles passing on vibrations like a row of dominos. Speed depends on how close together the dominos are — denser solids transmit fastest.',
  },
  glossary: [
    { term: 'Longitudinal wave', definition: 'A wave where the medium vibrates back-and-forth along the direction of travel.' },
    { term: 'Pitch', definition: 'How high or low a sound is — set by frequency.' },
    { term: 'Loudness', definition: 'How loud a sound is — set by amplitude. Measured in decibels (dB).' },
    { term: 'Compression', definition: 'A region of high pressure in a longitudinal wave.' },
    { term: 'Rarefaction', definition: 'A region of low pressure in a longitudinal wave.' },
  ],
  examQuestion: {
    prompt:
      "(a) State whether sound is transverse or longitudinal. (b) Explain why sound cannot travel through space. (c) State the effect of (i) increasing frequency and (ii) increasing amplitude on a sound. (4 marks)",
    maxMarks: 4,
    markSchemePoints: [
      'Longitudinal',
      'Sound needs particles to vibrate; space is a vacuum (no particles)',
      'Increasing frequency → higher pitch',
      'Increasing amplitude → louder sound',
    ],
  },
  practice: {
    warmUp: [
      { id: 'phys-10.7.w1', type: 'text', question: 'Sound is a ___ wave.', answer: 'longitudinal', hint1: '12 letters', hint2: 'Particles back-and-forth', hint3: 'Starts with l' },
      { id: 'phys-10.7.w2', type: 'choice', question: 'Higher pitch means…', options: ['Louder', 'Higher frequency', 'Higher amplitude', 'Slower'], answer: 'Higher frequency', hint1: 'More waves per second', hint2: 'Hz up', hint3: 'Linked to f' },
      { id: 'phys-10.7.w3', type: 'choice', question: 'Speed of sound in air is roughly…', options: ['33 m/s', '330 m/s', '3300 m/s', '3 × 10⁸ m/s'], answer: '330 m/s', hint1: 'Three digits', hint2: 'Slower than light', hint3: 'Approx 1/1,000,000 of light speed' },
    ],
    level1: [
      { id: 'phys-10.7.1', type: 'choice', question: 'Sound travels FASTEST in…', options: ['Air', 'Water', 'Steel', 'Vacuum'], answer: 'Steel', hint1: 'Densest medium', hint2: 'Particles closest', hint3: '~5000 m/s' },
      { id: 'phys-10.7.2', type: 'text', question: 'Loudness is measured in ___ .', answer: 'decibels', hint1: '8 letters', hint2: 'Symbol dB', hint3: 'Starts with d' },
    ],
    level2: [
      { id: 'phys-10.7.3', type: 'number', question: 'Thunder heard 9 s after lightning. Distance (km, using 330 m/s)?', answer: '3', hint1: '330 × 9 ÷ 1000', hint2: 'Single digit', hint3: 'About 3 km' },
      { id: 'phys-10.7.4', type: 'choice', question: 'Why CAN\u2019T sound travel through a vacuum?', options: ['Too cold', 'No particles to vibrate', 'No light', 'Magnetic'], answer: 'No particles to vibrate', hint1: 'Needs a medium', hint2: 'Sound IS particles vibrating', hint3: 'Empty space = no medium' },
    ],
    level3: [
      { id: 'phys-10.7.5', type: 'choice', question: 'A region of HIGH pressure in a sound wave is a…', options: ['Rarefaction', 'Compression', 'Trough', 'Crest'], answer: 'Compression', hint1: '11 letters', hint2: 'Particles bunched', hint3: 'Starts with c' },
      { id: 'phys-10.7.6', type: 'text', question: 'Higher amplitude makes a sound ___ .', answer: 'louder', hint1: '6 letters', hint2: 'More energy', hint3: 'Volume up' },
    ],
  },
};

// =====================================================================
// 10.8  HEARING
// =====================================================================
const HEARING_TOPIC = {
  id: 'phys-10.8',
  title: 'Hearing',
  slug: 'hearing',
  pageRef: 'Section 10 · Topic 8',
  difficulty: 'foundation',
  diagram: null,
  learn: {
    chunks: [
      {
        heading: 'Sound into the ear',
        body:
          "Sound waves enter the ear canal and reach the eardrum, which vibrates back and forth. Three tiny bones (the hammer, anvil and stirrup) amplify these vibrations and pass them on to the cochlea — a fluid-filled spiral. Tiny hair cells inside the cochlea convert the vibrations into nerve signals, which the auditory nerve carries to the brain.",
        check: {
          question: 'Where in the ear are sound vibrations turned into nerve signals?',
          options: ['Eardrum', 'Hammer', 'Cochlea', 'Outer ear'],
          answer: 'Cochlea',
          explainer: 'Hair cells in the cochlea fire nerve signals when vibrated.',
        },
      },
      {
        heading: 'Hearing range',
        body:
          'Healthy young humans can hear sounds with frequencies between roughly 20 Hz and 20,000 Hz (20 kHz). Frequencies BELOW 20 Hz are infrasound (felt by elephants, some whales). Frequencies ABOVE 20 kHz are ultrasound (used by bats, dolphins, and in medical scans). Hearing range narrows with age — most adults can\u2019t hear above ~15 kHz.',
        check: {
          question: 'Sound above 20,000 Hz is called…',
          options: ['Infrasound', 'Ultrasound', 'Pitch', 'Volume'],
          answer: 'Ultrasound',
          explainer: 'Ultra = above. Used by bats and in medical imaging.',
        },
      },
      {
        heading: 'Damage and protection',
        body:
          'Loud sounds (above ~85 dB) can damage hair cells in the cochlea. Damaged hair cells don\u2019t grow back — that\u2019s why hearing loss is often permanent. Protect hearing with ear plugs or noise-cancelling headphones; keep music volumes reasonable; take breaks from loud environments. Tinnitus (ringing in the ears) is often an early warning sign.',
        check: {
          question: 'Damage to hair cells in the cochlea is usually…',
          options: ['Reversible', 'Permanent', 'Healed by sleep', 'Cured by louder sounds'],
          answer: 'Permanent',
          explainer: 'Damaged hair cells don\u2019t regrow.',
        },
      },
    ],
    keyPoints: [
      'Path: ear canal → eardrum → hammer/anvil/stirrup → cochlea → auditory nerve → brain',
      'Healthy human range: ~20 Hz to ~20 kHz',
      'Below 20 Hz = infrasound; above 20 kHz = ultrasound',
      'Hearing range narrows with age',
      'Loud sounds damage cochlea hair cells permanently',
    ],
    workedExamples: [
      {
        problem: 'Why do people who go to lots of loud concerts often experience hearing loss?',
        steps: [
          { step: 1, instruction: 'Loudness', working: 'Concerts often exceed 100 dB' },
          { step: 2, instruction: 'Effect', working: 'High amplitude vibrations damage hair cells in the cochlea' },
          { step: 3, instruction: 'Recovery', working: 'Hair cells do not regenerate — damage is permanent' },
        ],
        answer: 'Loud sound permanently damages cochlear hair cells; these don\u2019t regrow.',
        commonMistake: 'Saying "the eardrum bursts" — that\u2019s extreme. Most hearing damage is silent damage to the cochlea.',
      },
    ],
    whyItWorks:
      'Your ear is essentially a beautifully engineered amplifier and frequency analyser. Treat it well — you only get one set of cochlear hair cells in a lifetime.',
  },
  glossary: [
    { term: 'Eardrum', definition: 'A thin membrane in the ear that vibrates when sound waves hit it.' },
    { term: 'Cochlea', definition: 'A fluid-filled spiral organ in the inner ear that contains hair cells.' },
    { term: 'Auditory nerve', definition: 'The nerve carrying signals from the cochlea to the brain.' },
    { term: 'Ultrasound', definition: 'Sound above 20 kHz; used by bats, dolphins and in medical scans.' },
    { term: 'Infrasound', definition: 'Sound below 20 Hz; some animals can sense it.' },
  ],
  examQuestion: {
    prompt:
      "(a) Describe the path of a sound wave through the ear from the ear canal to the brain. (b) Explain why hearing loss from loud noise is usually permanent. (c) State the human hearing range. (5 marks)",
    maxMarks: 5,
    markSchemePoints: [
      'Ear canal → eardrum → 3 small bones (hammer/anvil/stirrup) → cochlea → auditory nerve → brain',
      'Loud sounds damage hair cells in the cochlea',
      'These hair cells do not regenerate',
      'so the damage is permanent',
      '20 Hz to 20,000 Hz (20 kHz)',
    ],
  },
  practice: {
    warmUp: [
      { id: 'phys-10.8.w1', type: 'text', question: 'The thin membrane that vibrates when sound enters the ear is the ___ .', answer: 'eardrum', hint1: '7 letters', hint2: 'First vibrates', hint3: 'Compound word' },
      { id: 'phys-10.8.w2', type: 'choice', question: 'Sound above 20,000 Hz is…', options: ['Infrasound', 'Ultrasound', 'Loud', 'Quiet'], answer: 'Ultrasound', hint1: '10 letters', hint2: 'Used by bats', hint3: 'Above hearing range' },
      { id: 'phys-10.8.w3', type: 'choice', question: 'Damage to hair cells in the cochlea is usually…', options: ['Reversible', 'Permanent', 'Cured by sleep', 'Cured by silence'], answer: 'Permanent', hint1: 'They do not regrow', hint2: 'Once gone, gone', hint3: '9 letters' },
    ],
    level1: [
      { id: 'phys-10.8.1', type: 'text', question: 'The fluid-filled spiral in the inner ear is the ___ .', answer: 'cochlea', hint1: '7 letters', hint2: 'Snail-shaped', hint3: 'Contains hair cells' },
      { id: 'phys-10.8.2', type: 'choice', question: 'Healthy young human hearing range?', options: ['1–10,000 Hz', '20–20,000 Hz', '100–100,000 Hz', '0–1 Hz'], answer: '20–20,000 Hz', hint1: 'Two endpoints', hint2: 'Lower bound 20', hint3: 'Up to 20 kHz' },
    ],
    level2: [
      { id: 'phys-10.8.3', type: 'choice', question: 'Why might older adults not hear high-pitched alarms?', options: ['Damage from loud sounds + age narrows hearing range', 'They ignore them', 'Aliens', 'Inflation'], answer: 'Damage from loud sounds + age narrows hearing range', hint1: 'High frequencies lost first', hint2: 'Hair-cell damage cumulative', hint3: 'Age-related' },
      { id: 'phys-10.8.4', type: 'text', question: 'Sound below 20 Hz is called ___ .', answer: 'infrasound', hint1: '10 letters', hint2: 'Below hearing', hint3: 'Felt by elephants' },
    ],
    level3: [
      { id: 'phys-10.8.5', type: 'choice', question: 'Tinnitus is…', options: ['A hearing aid', 'Ringing in the ears (often a damage warning)', 'Total deafness', 'A musical instrument'], answer: 'Ringing in the ears (often a damage warning)', hint1: 'Persistent ringing', hint2: 'Linked to hair-cell damage', hint3: 'Ear condition' },
      { id: 'phys-10.8.6', type: 'text', question: 'Three small bones in the ear amplify vibrations: hammer, anvil and ___ .', answer: 'stirrup', hint1: '7 letters', hint2: 'Last of the three', hint3: 'Starts with s' },
    ],
  },
};

// =====================================================================
// 10.9  ENERGY AND WAVES
// =====================================================================
const ENERGY_WAVES_TOPIC = {
  id: 'phys-10.9',
  title: 'Energy and Waves',
  slug: 'energy-and-waves',
  pageRef: 'Section 10 · Topic 9',
  difficulty: 'higher',
  diagram: null,
  learn: {
    chunks: [
      {
        heading: 'Waves carry energy',
        body:
          'Every wave carries energy. Bigger amplitude → more energy. A small ripple barely moves a stick on the water; a big ocean wave can flatten a coastal building. Sound: louder = more energy. Light: brighter = more energy. Earthquakes: bigger Richter number = bigger amplitude = far more destruction.',
        check: {
          question: 'A wave with bigger amplitude carries…',
          options: ['Less energy', 'More energy', 'The same energy', 'No energy'],
          answer: 'More energy',
          explainer: 'Energy ∝ amplitude². Big waves are far more energetic than small ones.',
        },
      },
      {
        heading: 'Wave equation',
        body:
          'Wave speed (m/s) = frequency (Hz) × wavelength (m). v = f × λ. So if you know two of the three, you can find the third. Example: a sound wave with frequency 100 Hz and wavelength 3.3 m has speed 100 × 3.3 = 330 m/s — the speed of sound in air.',
        check: {
          question: 'A wave has frequency 5 Hz and wavelength 4 m. Speed?',
          options: ['1.25 m/s', '9 m/s', '20 m/s', '0.8 m/s'],
          answer: '20 m/s',
          explainer: 'v = f × λ = 5 × 4 = 20 m/s.',
        },
      },
      {
        heading: 'Real applications',
        body:
          'Knowing wave speed = frequency × wavelength is essential for: radio (different stations broadcast at different frequencies, so different wavelengths); ultrasound scans (use very short wavelengths to image babies/organs); earthquake monitoring (waves of different speeds let scientists triangulate the epicentre); musical instruments (string length sets the wavelength → fixed frequencies → fixed pitches).',
        check: {
          question: 'Why are short-wavelength waves good for ultrasound scans?',
          options: ['Travel further', 'Cheaper', 'Image fine detail', 'Travel through walls'],
          answer: 'Image fine detail',
          explainer: 'Smaller wavelength → can resolve smaller objects.',
        },
      },
    ],
    keyPoints: [
      'Bigger amplitude → more energy (energy ∝ amplitude²)',
      'Wave equation: v = f × λ',
      'Speed in m/s, frequency in Hz, wavelength in m',
      'Short wavelengths → fine detail (ultrasound)',
      'Different radio stations = different frequencies',
    ],
    workedExamples: [
      {
        problem: 'A radio station broadcasts at 100 MHz (100,000,000 Hz). The wave travels at 3 × 10⁸ m/s (speed of light). Find the wavelength.',
        steps: [
          { step: 1, instruction: 'Rearrange v = fλ', working: 'λ = v ÷ f' },
          { step: 2, instruction: 'Substitute', working: '3 × 10⁸ ÷ 1 × 10⁸ = 3 m' },
        ],
        answer: 'Wavelength = 3 m.',
        commonMistake: 'Mixing up units. Make sure all are in m, Hz and m/s.',
      },
    ],
    whyItWorks:
      'The simple relationship v = fλ links every wave you\u2019ll meet — water, sound, light, radio, ultrasound. Master it and you can switch between wavelength and frequency for any wave in any medium.',
  },
  glossary: [
    { term: 'Wave equation', definition: 'v = f × λ : speed = frequency × wavelength.' },
    { term: 'Energy of a wave', definition: 'Increases with amplitude — proportional to amplitude squared.' },
    { term: 'Ultrasound scan', definition: 'A medical imaging technique using high-frequency, short-wavelength sound.' },
  ],
  examQuestion: {
    prompt:
      'A water wave has wavelength 0.4 m and frequency 5 Hz. (a) Calculate the speed. (b) State the relationship between amplitude and energy. (c) Suggest why ultrasound scans use very high frequencies. (5 marks)',
    maxMarks: 5,
    markSchemePoints: [
      'v = f × λ',
      '= 5 × 0.4 = 2 m/s',
      'Bigger amplitude → more energy (energy ∝ amplitude²)',
      'Higher frequency → smaller wavelength',
      'Smaller wavelength → resolves finer detail in body imaging',
    ],
  },
  practice: {
    warmUp: [
      { id: 'phys-10.9.w1', type: 'text', question: 'Wave equation: v = f × ___ .', answer: 'λ', hint1: '1 character', hint2: 'Greek letter', hint3: 'Wavelength symbol', altAnswers: ['lambda', 'wavelength'] },
      { id: 'phys-10.9.w2', type: 'number', question: 'f = 10 Hz, λ = 0.5 m. v (m/s)?', answer: '5', hint1: '10 × 0.5', hint2: 'Single digit', hint3: 'Five' },
      { id: 'phys-10.9.w3', type: 'choice', question: 'Bigger amplitude →', options: ['Less energy', 'More energy', 'Same', 'Zero energy'], answer: 'More energy', hint1: 'Energy ∝ A²', hint2: 'Taller crest', hint3: 'Stronger wave' },
    ],
    level1: [
      { id: 'phys-10.9.1', type: 'number', question: 'f = 4 Hz, λ = 2.5 m. v?', answer: '10', hint1: 'Multiply', hint2: '4 × 2.5', hint3: 'Two digits' },
      { id: 'phys-10.9.2', type: 'choice', question: 'A radio wave with high frequency has a ___ wavelength.', options: ['Long', 'Short', 'Same', 'Zero'], answer: 'Short', hint1: 'Inverse relationship', hint2: 'Higher f → shorter λ', hint3: 'Same as ultrasound' },
    ],
    level2: [
      { id: 'phys-10.9.3', type: 'number', question: 'v = 300 m/s, f = 100 Hz. λ?', answer: '3', hint1: 'v ÷ f', hint2: 'Single digit', hint3: 'Three' },
      { id: 'phys-10.9.4', type: 'choice', question: 'Why doubled amplitude DOESN\u2019T mean doubled energy?', options: ['Random', 'Energy ∝ amplitude squared', 'Energy ∝ frequency only', 'Energy doesn\u2019t depend on amplitude'], answer: 'Energy ∝ amplitude squared', hint1: 'Square law', hint2: 'A² relationship', hint3: 'Doubled A → 4× energy' },
    ],
    level3: [
      { id: 'phys-10.9.5', type: 'number', question: 'Light v = 3 × 10⁸ m/s. f = 6 × 10¹⁴ Hz. λ (× 10⁻⁷ m)?', answer: '5', hint1: 'v ÷ f = 5 × 10⁻⁷ m', hint2: 'Single digit', hint3: 'Five' },
      { id: 'phys-10.9.6', type: 'text', question: 'Sound speed = ___ × wavelength.', answer: 'frequency', hint1: '9 letters', hint2: 'Symbol f', hint3: 'Starts with f' },
    ],
  },
};

// ---------- assemble the section ----------
const BASE_TOPICS = [
  WATER_WAVES_TOPIC,
  LIGHT_TOPIC,
  REFLECTION_TOPIC,
  REFRACTION_TOPIC,
  HOW_WE_SEE_TOPIC,
  COLOUR_TOPIC,
  SOUND_TOPIC,
  HEARING_TOPIC,
  ENERGY_WAVES_TOPIC,
];

// authoredTopics.json overrides any topic by id (for the PDF pipeline).
export const wavesTopics = BASE_TOPICS.map((t) =>
  AUTHORED_BY_ID[t.id] ? AUTHORED_BY_ID[t.id] : t,
);
